/**
 * useEasterEggs — Hook for Easter egg state management.
 *
 * DDD co-locatie: Lives in mascot domain alongside easterEggs.ts registry.
 * Persistence is delegated to `dataLayer.getDiscoveredEasterEggs` /
 * `saveDiscoveredEasterEggs`.
 *
 * GLOBAL STORE (audit §11.8 fix, ronde 4 2026):
 * Previously every call-site created its own React-local `useState` for the
 * discovered-eggs set. After FloatingDjay triggered an egg, Sidebar /
 * MobileNav / CollectionPage kept showing stale state because each had its
 * own independent instance. Now uses a module-level store + `useSyncExternal
 * Store` (same pattern as `useDjaySelection`) so all consumers stay in sync.
 *
 * Usage:
 *   const { triggerEgg, isDiscovered, discoveredEggs } = useEasterEggs()
 *   if (triggerEgg('rapid-tap').triggered) { ... }
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { useAppStore } from '../app/hooks/useAppStore'
import { dataLayer } from '../data/dataLayer'
import { soundManager } from '../utils/soundManager'
import { hapticManager } from '../utils/hapticManager'
import * as logger from '../utils/logger'
import {
  EASTER_EGGS,
  type EasterEggId,
  type EasterEggReaction,
} from './easterEggs'

// ── Types ────────────────────────────────────────────────────

interface EasterEggTriggerResult {
  triggered: boolean
  reaction: EasterEggReaction | null
}

interface UseEasterEggsReturn {
  /** Try to trigger an Easter egg. Returns reaction if successful. */
  triggerEgg: (id: EasterEggId) => EasterEggTriggerResult
  /** Check if an egg has been discovered before */
  isDiscovered: (id: EasterEggId) => boolean
  /** Set of all discovered egg IDs */
  discoveredEggs: Set<EasterEggId>
  /** Total number of discovered eggs */
  discoveredCount: number
}

// ── Module-level shared store ────────────────────────────────
// All useEasterEggs() instances subscribe to this store so a discovery in
// FloatingDjay is instantly visible to Sidebar/MobileNav/CollectionPage.

type Listener = () => void

let _discoveredEggs: Set<EasterEggId> = new Set()
let _currentAccountId: string | null = null
/** Monotonic generation token. Bumped on account-switch load AND on every
 *  explicit trigger. An in-flight load only applies if the generation
 *  hasn't moved — same race-guard as useDjaySelection. */
let _generation = 0
const _listeners = new Set<Listener>()

function getSnapshot(): Set<EasterEggId> {
  return _discoveredEggs
}

function subscribe(listener: Listener): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

function _notify() {
  _listeners.forEach((l) => l())
}

async function _loadForAccount(accountId: string | null) {
  _generation += 1
  const gen = _generation
  _currentAccountId = accountId
  try {
    const [ids, longPressLegacy] = await Promise.all([
      dataLayer.getDiscoveredEasterEggs(accountId),
      dataLayer.getDjayLongPressDiscovered(accountId).catch(() => false),
    ])
    if (_currentAccountId !== accountId || _generation !== gen) return
    const next = new Set(ids as EasterEggId[])
    // One-shot migration: legacy long-press flag → egg set (audit S4)
    if (longPressLegacy && !next.has('long-press-goud')) {
      next.add('long-press-goud')
      void dataLayer
        .saveDiscoveredEasterEggs(accountId, [...next])
        .catch((err) => {
          logger.warn(
            '[useEasterEggs] failed to persist long-press migration',
            err,
          )
        })
    }
    _discoveredEggs = next
    _notify()
  } catch (err) {
    logger.warn('[useEasterEggs] failed to load discovered eggs', err)
  }
}

function _triggerEgg(id: EasterEggId): EasterEggTriggerResult {
  const egg = EASTER_EGGS[id]
  if (!egg) return { triggered: false, reaction: null }

  const current = _discoveredEggs

  if (!egg.repeatable && current.has(id)) {
    return { triggered: false, reaction: null }
  }

  if (!current.has(id)) {
    _generation += 1
    const next = new Set(current)
    next.add(id)
    _discoveredEggs = next
    _notify()
    // Persist via dataLayer — fire-and-forget, errors are non-fatal.
    void dataLayer
      .saveDiscoveredEasterEggs(_currentAccountId, [...next])
      .catch((err) => {
        logger.warn('[useEasterEggs] failed to save discovered eggs', err)
      })
  }

  if (egg.sound) {
    const play = soundManager[egg.sound] as (() => void) | undefined
    if (play) play()
  }

  hapticManager.hapticCelebration()

  return { triggered: true, reaction: egg.reaction }
}

// ── Public hook ──────────────────────────────────────────────

export function useEasterEggs(): UseEasterEggsReturn {
  const { activeAccount } = useAppStore()
  const accountId = activeAccount?.id ?? null

  // Single effect: load on mount AND on account-switch.
  useEffect(() => {
    void _loadForAccount(accountId)
  }, [accountId])

  // Subscribe to the shared store
  const discoveredEggs = useSyncExternalStore(subscribe, getSnapshot)

  const isDiscovered = useCallback(
    (id: EasterEggId) => discoveredEggs.has(id),
    [discoveredEggs],
  )

  // triggerEgg is stable — no closure over account-scoped state because
  // the module-level store owns everything.
  const triggerEgg = useCallback(
    (id: EasterEggId): EasterEggTriggerResult => _triggerEgg(id),
    [],
  )

  return {
    triggerEgg,
    isDiscovered,
    discoveredEggs,
    discoveredCount: discoveredEggs.size,
  }
}

/**
 * useLongPressDiscovered — Convenience selector for "has the user
 * discovered the long-press easter egg?".
 *
 * Now backed by the shared module store (ronde 4 2026): a discovery in
 * FloatingDjay is immediately visible to all callers. Before the global
 * store, Sidebar/MobileNav/CollectionPage each had their own React-local
 * state that wouldn't see in-session updates from FloatingDjay until next
 * account-switch.
 */
export function useLongPressDiscovered(): boolean {
  const { isDiscovered } = useEasterEggs()
  return isDiscovered('long-press-goud')
}
